import { nanoid } from "nanoid";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, customerReqConfig } from "../../include/api";
import { ADD, DELETE, EDIT, ERROR, SUCCESS } from "../../include/constant";
import { getCustomer, preventClick } from "../../include/function";
import { useNotification } from "../../Toast/ToastProvider";
import Error from "../add-ons/Error";
import Spinner from "../add-ons/Spinner";
import AddressForm from "../common/AddressForm";
import Modal from "../add-ons/Modal";
import "../../styles/Checkout.css";

const CustomerAddress = () => {
    const user = getCustomer();
    const toast = useNotification();
    const [showYesOrNo, setShowYesOrNo] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [deleteloading, setDeleteLoading] = useState(false);
    const [addressList, setAddressList] = useState([]);
    const [type, setType] = useState("");
    const [id, setId] = useState("");
    const [formContext, setFormContext] = useState(null); // NEW: to handle delivery vs billing

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    useEffect(() => {
        if (user.id && user.id !== "") {
            getAddress(user.id);
        }
    }, [user.id]);

    const getAddress = (id) => {
        setFetchLoading(true);
        api
            .get("/customer_address/?customerId=" + id, customerReqConfig())
            .then((response) => {
                if (response.status === 200) {
                    setAddressList(response.data.data);
                }
            })
            .catch((error) => { })
            .finally(() => setFetchLoading(false));
    };

    const handleYes = () => {
        setDeleteLoading(true);
        api
            .delete("/customer_address/?id=" + id, customerReqConfig())
            .then((response) => {
                if (response.status === 200) {
                    setAddressList(addressList.filter((address) => String(address.id) !== String(id)));
                    toast({ type: SUCCESS, message: response.data.message });
                    setShowYesOrNo(false);
                }
            })
            .catch((error) => {
                toast({
                    type: ERROR,
                    message: error.response?.data?.message || error.message,
                });
            })
            .finally(() => setDeleteLoading(false));
    };

    const handleNo = () => {
        setShowYesOrNo(false);
        setId("");
        setType("");
    };

    const handleType = (type, id) => {
        setType(type);
        if (type === EDIT || type === DELETE) setId(id);
        if (type === DELETE) setShowYesOrNo(true);
        scrollToTop();
    };

    // Unified save handler for both delivery and billing
    const onSave = (data) => {
        setAddressList((prev) => {
            const updated = [...prev];
            const index = updated.findIndex((a) => String(a.id) === String(data.id));
            if (index !== -1) {
                updated[index] = { ...data };
            } else {
                updated.push(data);
            }
            return updated;
        });
        setFormContext(null);
        scrollToTop();
    };

    const onCancel = () => {
        setFormContext(null);
        scrollToTop();
    };

    return (
        <>
            {showYesOrNo && (
                <Modal
                    visible={showYesOrNo}
                    onClose={handleNo}
                    headerText="Do you really want to delete this address?"
                    size="lg"
                    centered
                >
                    <div className="d-flex justify-content-center gap-5 mt-3">
                        <button className="btn btn-main" onClick={handleYes} disabled={deleteloading}>
                            {deleteloading ? "Deleting..." : "Yes"}
                        </button>
                        <button className="btn btn-main" onClick={handleNo}>
                            No
                        </button>
                    </div>
                </Modal>
            )}

            <div className="container">
                <div className="p-32">
                    <h4 className="text-xl mb-32">Address</h4>

                    {/* Conditional rendering based on formContext */}
                    {formContext?.mode === ADD && formContext?.addressType === "delivery" ? (
                        <AddressForm
                            reqConfig={customerReqConfig()}
                            type={ADD}
                            userId={user.id}
                            title="Add Address"
                            onSave={onSave}
                            onCancel={onCancel}
                        />
                    ) : formContext?.mode === ADD && formContext?.addressType === "billing" ? (
                        <AddressForm
                            reqConfig={customerReqConfig()}
                            type={ADD}
                            userId={user.id}
                            title="Add Billing Address"
                            forceAddressType="billing"
                            showGstField={true}
                            onSave={onSave}
                            onCancel={onCancel}
                        />
                    ) : (
                        <div className="py-3 d-flex gap-3">
                            {/* Regular Add Address (delivery/home/work) */}
                            <button
                                className={`btn btn-sm flex-center fw-bold rounded-16 py-16 btn-main text-white`}
                                style={{ width: "150px", border: "none" }}
                                onClick={(e) =>
                                    preventClick(e, () =>
                                        setFormContext({ mode: ADD, addressType: "delivery" })
                                    )
                                }
                            >
                                Add Home/Work Address
                            </button>

                            {/* Dedicated Add Billing Address */}
                            <button
                                className={`btn btn-sm flex-center fw-bold rounded-16 py-16 btn-main text-white`}
                                style={{ width: "150px", border: "none" }}
                                onClick={(e) =>
                                    preventClick(e, () =>
                                        setFormContext({ mode: ADD, addressType: "billing" })
                                    )
                                }
                            >
                                Add Billing Address
                            </button>
                        </div>
                    )}

                    {fetchLoading ? (
                        <div className="py-5">
                            <Spinner color="danger" />
                        </div>
                    ) : addressList.length > 0 ? (
                        addressList.map((address) => {
                            return formContext?.mode === EDIT &&
                                formContext?.addressId === address.id ? (
                                <AddressForm
                                    key={nanoid()}
                                    id={address.id}
                                    reqConfig={customerReqConfig()}
                                    title={
                                        address.addressType === "billing"
                                            ? "Edit Billing Address"
                                            : "Edit Address"
                                    }
                                    type={EDIT}
                                    userId={user.id}
                                    editAddress={address}
                                    forceAddressType={
                                        address.addressType === "billing" ? "billing" : undefined
                                    }
                                    showGstField={address.addressType === "billing"}
                                    onSave={onSave}
                                    onCancel={onCancel}
                                />
                            ) : (
                                <div key={nanoid()} className="w-100 p-10">
                                    <div className="address-card1 d-flex justify-content-between align-items-start gap-10 p-16 rounded-8 border border-gray-100">
                                        <div className="d-flex align-items-start gap-12">
                                            <div
                                                className="bg-gray-100 p-2 rounded-8 d-flex justify-content-center align-items-center"
                                                style={{ width: "90px", height: "90px" }}
                                            >
                                                {address.addressType === "home" ? (
                                                    <i className="ph ph-house" style={{ fontSize: "60px" }} />
                                                ) : address.addressType === "billing" ? (
                                                    <i className="ph ph-receipt" style={{ fontSize: "60px" }} />
                                                ) : (
                                                    <i className="ph ph-buildings" style={{ fontSize: "60px" }} />
                                                )}
                                            </div>
                                            <div style={{ width: "450px", textWrap: "wrap" }}>
                                                <div className="text-uppercase small fw-bold mt-2 me-3 text-nowrap">
                                                    <span className="text-black">{address.addressType}</span>
                                                </div>
                                                {address.addressType === "billing" && (
                                                    <p className="mb-1">GSTIN: {address.gstNo || "N/A"}</p>
                                                )}
                                                <p className="mb-1">{address.name}</p>
                                                <p className="mb-1">{address.phoneNumber}</p>
                                                <p className="mb-1">{address.address}</p>
                                                <p className="mb-0">
                                                    {address.locality}, {address.city}, {address.state} - {address.pin}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="d-flex flex-column align-items-end gap-2" style={{ width: "120px" }}>
                                            <button
                                                className="btn bg-gray-50 text-heading hover-bg-main-600 hover-text-white py-11 px-24 rounded-8 w-100 mt-16 mb-32"
                                                onClick={(e) =>
                                                    preventClick(e, () =>
                                                        setFormContext({
                                                            mode: EDIT,
                                                            addressId: address.id,
                                                            addressType: address.addressType,
                                                        })
                                                    )
                                                }
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="btn bg-gray-50 text-heading hover-bg-main-600 hover-text-white py-11 px-24 rounded-8 w-100"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleType(DELETE, address.id);
                                                }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <Error
                            mainText="Sorry, no address found!"
                            secondaryText={
                                <div className="d-flex gap-3">
                                    <button
                                        className={`btn btn-sm flex-center fw-bold rounded-16 py-16 btn-main text-white`}
                                        style={{ width: "150px", border: "none" }}
                                        onClick={(e) =>
                                            preventClick(e, () =>
                                                setFormContext({ mode: ADD, addressType: "delivery" })
                                            )
                                        }
                                    >
                                        Add Home/Work Address

                                    </button>
                                    <button
                                        className={`btn btn-sm flex-center fw-bold rounded-16 py-16 btn-main text-white`}
                                        style={{ width: "150px", border: "none" }}
                                        onClick={(e) =>
                                            preventClick(e, () =>
                                                setFormContext({ mode: ADD, addressType: "billing" })
                                            )
                                        }
                                    >
                                        Add Billing Address
                                    </button>
                                </div>
                            }
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default CustomerAddress;